import { Box } from "@twilio-paste/core/box";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import type { AnimationEvent } from "react";

import { MessagingCanvasPhase } from "./MessagingCanvasPhase";
import { AppState, EngagementPhase } from "../store/definitions";
import { PreEngagementFormPhase } from "./PreEngagementFormPhase";
import { LoadingPhase } from "./LoadingPhase";
import { EntryPoint } from "./EntryPoint";
import { innerContainerStyles, outerContainerStyles } from "./styles/RootContainer.styles";

const getPhaseComponent = (phase: EngagementPhase) => {
    switch (phase) {
        case EngagementPhase.Loading:
            return <LoadingPhase />;
        case EngagementPhase.MessagingCanvas:
            return <MessagingCanvasPhase />;
        case EngagementPhase.PreEngagementForm:
        default:
            return <PreEngagementFormPhase />;
    }
};

export function RootContainer() {
    const { currentPhase, expanded } = useSelector(({ session }: AppState) => ({
        currentPhase: session.currentPhase,
        expanded: session.expanded
    }));

    // Posição/borda configuráveis (brand-config, runtime). Ausente = comportamento atual.
    const launcherPosition = useSelector((state: AppState) => state.config?.launcherPosition);
    const borderRadius = useSelector((state: AppState) => state.config?.borderRadius);
    // sameOnMobile: default true (só vira false se a config disser explicitamente).
    const sameOnMobile = launcherPosition?.sameOnMobile !== false;
    const windowBorderRadius = typeof borderRadius === "number" ? borderRadius : 20;

    // Keep the window mounted while the slide-down (exit) animation plays, then unmount.
    const [isMounted, setIsMounted] = useState(expanded);

    useEffect(() => {
        if (expanded) {
            setIsMounted(true);
        }
    }, [expanded]);

    const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
        // Ignore animations bubbling up from children; unmount only after the exit animation.
        if (event.target === event.currentTarget && !expanded) {
            setIsMounted(false);
        }
    };

    return (
        <Box>
            {/* z-index máximo: o widget é injetado no site da marca e PRECISA sobrepor todo o
                conteúdo host (no mobile abre fullscreen). Cria o stacking context no topo —
                a janela fullscreen (filha) herda e cobre tudo. Inline porque tokens do Paste
                (zIndexNN) são baixos demais p/ vencer z-indexes de sites de terceiros. */}
            <Box
                {...outerContainerStyles}
                data-test="webchat-root"
                data-wc-same-mobile={String(sameOnMobile)}
                style={{ zIndex: 2147483647 }}
            >
                {isMounted && (
                    <Box
                        data-test="root-container"
                        className={expanded ? "webchat-window-enter" : "webchat-window-exit"}
                        onAnimationEnd={handleAnimationEnd}
                        // Borda da janela: configurável via brand-config; default 20px. Mobile zera (mobile-overrides).
                        style={{ borderRadius: `${windowBorderRadius}px` }}
                        {...innerContainerStyles}
                    >
                        {getPhaseComponent(currentPhase)}
                    </Box>
                )}
                <EntryPoint />
            </Box>
        </Box>
    );
}
